import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Loader2, Save, ChevronDown, ChevronRight, GraduationCap, CheckCircle2 } from "lucide-react";
import { useRoninLevelPicker } from "@/pages/admin/useRoninLevelPicker";

/**
 * Admin level-picker — bulk assign cues to beginner / intermediate / advanced.
 *
 *   - All cues are listed grouped by category (using the dynamic categories collection).
 *   - Each cue row has a 3-segment radio (BEG · INT · ADV).
 *   - "Pending changes" counter appears at the top whenever any cue's level has been changed but
 *     not yet saved. Save button POSTs to /api/admin/ronin-box/cues/batch-level.
 *   - Filter chips above the list let you scope to one level at a time.
 *   - Per-category mini-stats: "12 BEG · 8 INT · 4 ADV" so the coach can balance counts.
 */
const LEVELS = ["beginner", "intermediate", "advanced"];
const LEVEL_LABEL = { beginner: "Beg", intermediate: "Int", advanced: "Adv" };
const LEVEL_COLOR = {
  beginner:     "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
  intermediate: "border-amber-500/50 bg-amber-500/10 text-amber-400",
  advanced:     "border-red-500/50 bg-red-500/10 text-red-400",
};

export default function RoninLevelPicker() {
  const {
    cues, setCues, categories, modes, loading, dirty, dirtyModes, dirtyWeights, filterLevel, setFilterLevel, search, setSearch, collapsed, saving, load, effectiveLevels, toggleLevel, dirtyCount, effectiveModes, effectiveWeight, setWeight, toggleMode, bulkSet, reset, save, expandSingleTierCues, visibleCues, grouped, categoryStats, totalStats, toggleCollapsed, categoryOrder,
  } = useRoninLevelPicker();


  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="ronin-level-picker-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/ronin-box" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white font-bold mb-2 inline-flex items-center gap-1" data-testid="level-picker-back">
            <ArrowLeft className="h-3 w-3" /> Back to Ronin Box
          </Link>
          <h1 className="font-display uppercase text-4xl text-white tracking-tight flex items-center gap-3">
            <GraduationCap className="h-7 w-7 text-[#FF331F]" />
            Level & Mode assignments
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            For every cue, pick the levels it belongs to (any combination — Beg + Adv but not Int is fine), the modes it should fire in (Shadow / Bag / Defense / any custom mode you&apos;ve added), and a <span className="text-white font-bold">weight (1-10)</span> that controls how often it plays relative to its category mates. Empty modes means &quot;only available in Custom mode&quot;.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-zinc-500">
            <span className="text-emerald-400 font-bold">{totalStats.beginner}</span> beg ·{" "}
            <span className="text-amber-400 font-bold">{totalStats.intermediate}</span> int ·{" "}
            <span className="text-red-400 font-bold">{totalStats.advanced}</span> adv
          </div>
          <Button
            variant="outline" onClick={reset} disabled={dirtyCount === 0 || saving}
            className="border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-[10px]"
            data-testid="level-picker-reset"
          >Reset</Button>
          <Button
            onClick={save} disabled={dirtyCount === 0 || saving}
            className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs disabled:opacity-50"
            data-testid="level-picker-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-2" />}
            Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
          </Button>
        </div>
      </div>

      {/* Controls: search + level filter chips + bulk-set */}
      <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cues…"
              className="pl-9 bg-[#050505] border-[#27272A] text-white rounded-sm h-9"
              data-testid="level-picker-search"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-2">Filter:</span>
            {["all", ...LEVELS].map((lvl) => (
              <button
                key={lvl}
                onClick={() => setFilterLevel(lvl)}
                className={`px-2.5 py-1 border rounded-sm text-[10px] uppercase tracking-widest font-bold transition-colors ${filterLevel === lvl ? "border-[#FF331F] bg-[#FF331F]/15 text-[#FF331F]" : "border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white"}`}
                data-testid={`level-picker-filter-${lvl}`}
              >{lvl === "all" ? "All" : LEVEL_LABEL[lvl]}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-[#1a1a1a] pt-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Bulk set visible to:</span>
          {LEVELS.map((lvl) => (
            <button
              key={lvl}
              onClick={() => bulkSet(lvl)}
              className={`px-2.5 py-1 border rounded-sm text-[10px] uppercase tracking-widest font-bold transition-colors ${LEVEL_COLOR[lvl]} hover:opacity-80`}
              data-testid={`level-picker-bulk-${lvl}`}
            >{LEVEL_LABEL[lvl]}</button>
          ))}
          <span className="text-[10px] text-zinc-600">({visibleCues.length} visible)</span>
          <button
            onClick={expandSingleTierCues}
            className="ml-auto px-2.5 py-1 border border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white rounded-sm text-[10px] uppercase tracking-widest font-bold"
            data-testid="level-picker-expand-single"
            title="Find every cue currently tagged with only one level and stage it to all three. Run this if newly-created cues are missing from Intermediate / Advanced workouts."
          >
            Expand single-tier cues
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading cues…
        </div>
      ) : visibleCues.length === 0 ? (
        <p className="text-sm text-zinc-500 py-10 text-center" data-testid="level-picker-empty">No cues match.</p>
      ) : (
        <div className="space-y-3">
          {categoryOrder.map((catId) => {
            const list = grouped[catId];
            if (!list || list.length === 0) return null;
            const catMeta = categories.find((c) => c.id === catId);
            const catLabel = catMeta?.label || catId;
            const stats = categoryStats[catId] || { beginner: 0, intermediate: 0, advanced: 0 };
            const isCollapsed = collapsed.has(catId);
            return (
              <div key={catId} className="border border-[#27272A] rounded-sm bg-[#0A0A0A]" data-testid={`level-picker-cat-${catId}`}>
                <button
                  onClick={() => toggleCollapsed(catId)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-[#0F0F0F] border-b border-[#27272A] hover:bg-[#141414]"
                >
                  <div className="flex items-center gap-3">
                    {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
                    <span className="font-display text-base uppercase tracking-tight text-white">{catLabel}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{list.length}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-3">
                    <span className="text-emerald-400">{stats.beginner} BEG</span>
                    <span className="text-amber-400">{stats.intermediate} INT</span>
                    <span className="text-red-400">{stats.advanced} ADV</span>
                  </div>
                </button>
                {!isCollapsed && (
                  <ul className="divide-y divide-[#1a1a1a]">
                    {list.map((c) => {
                      const lvls = effectiveLevels(c);
                      const savedLevels = (c.levels && c.levels.length > 0)
                        ? c.levels
                        : (c.level === "advanced" ? ["advanced"]
                           : c.level === "intermediate" ? ["intermediate", "advanced"]
                           : ["beginner", "intermediate", "advanced"]);
                      const isDirty = c.id in dirty;
                      return (
                        <li
                          key={c.id}
                          className={`px-4 py-2 grid grid-cols-[1fr_auto] gap-3 items-center ${(isDirty || c.id in dirtyModes || c.id in dirtyWeights) ? "bg-[#FF331F]/5" : ""}`}
                          data-testid={`level-picker-row-${c.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {(isDirty || c.id in dirtyModes || c.id in dirtyWeights) && <CheckCircle2 className="h-3 w-3 text-[#FF331F] shrink-0" />}
                            <span className="text-sm text-white truncate">{c.text}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Weight dropdown (1-10). Higher weight = cue plays more often. */}
                            {(() => {
                              const savedWeight = Number.isFinite(c.weight) ? Math.max(1, Math.min(10, c.weight)) : 1;
                              const w = effectiveWeight(c);
                              const isWDirty = c.id in dirtyWeights;
                              return (
                                <label
                                  className="inline-flex items-center gap-1.5 border border-[#27272A] rounded-sm bg-[#050505] pl-2 pr-1 h-[26px]"
                                  title="How often this cue plays relative to its category mates (1 = rare, 10 = very frequent)"
                                >
                                  <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Wt</span>
                                  <select
                                    value={w}
                                    onChange={(e) => setWeight(c.id, savedWeight, e.target.value)}
                                    className={`bg-transparent text-[11px] font-bold uppercase tracking-widest border-0 outline-none cursor-pointer pr-1 ${isWDirty ? "text-[#FF331F]" : "text-white"}`}
                                    data-testid={`level-picker-weight-${c.id}`}
                                    aria-label={`Weight for "${c.text}"`}
                                  >
                                    {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                                      <option key={n} value={n} className="bg-[#0A0A0A] text-white">{n}</option>
                                    ))}
                                  </select>
                                </label>
                              );
                            })()}
                            {/* Mode membership row (shadow / bag / defense / + admin-added modes) */}
                            <div className="inline-flex border border-[#27272A] rounded-sm overflow-hidden" role="group" aria-label={`Modes for "${c.text}"`}>
                              {modes.map((m) => {
                                const cueModes = effectiveModes(c);
                                const savedModes = Array.isArray(c.modes) ? c.modes : [];
                                const isActive = cueModes.includes(m.id);
                                const short = (m.label || m.id).slice(0, 3).toUpperCase();
                                return (
                                  <button
                                    key={m.id}
                                    type="button"
                                    aria-pressed={isActive}
                                    onClick={() => toggleMode(c.id, savedModes, m.id)}
                                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors border-r border-[#27272A] last:border-r-0 ${isActive ? "bg-[#3B82F6]/15 text-[#60A5FA]" : "bg-[#050505] text-zinc-500 hover:text-white"}`}
                                    data-testid={`level-picker-mode-${c.id}-${m.id}`}
                                    title={isActive ? `Remove from ${m.label}` : `Add to ${m.label}`}
                                  >{short}</button>
                                );
                              })}
                            </div>
                            {/* Level row */}
                            <div className="inline-flex border border-[#27272A] rounded-sm overflow-hidden" role="group" aria-label={`Levels for "${c.text}"`}>
                              {LEVELS.map((lv) => {
                                const isActive = lvls.includes(lv);
                                return (
                                  <button
                                    key={lv}
                                    type="button"
                                    aria-pressed={isActive}
                                    onClick={() => toggleLevel(c.id, savedLevels, lv)}
                                    className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors border-r border-[#27272A] last:border-r-0 ${isActive ? LEVEL_COLOR[lv] : "bg-[#050505] text-zinc-500 hover:text-white"}`}
                                    data-testid={`level-picker-set-${c.id}-${lv}`}
                                    title={isActive ? `Remove ${LEVEL_LABEL[lv]}` : `Add ${LEVEL_LABEL[lv]}`}
                                  >{LEVEL_LABEL[lv]}</button>
                                );
                              })}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dirtyCount > 0 && (
        <div className="fixed bottom-4 right-4 border border-[#FF331F]/40 bg-[#0A0A0A] rounded-sm p-3 shadow-lg flex items-center gap-3 z-50" data-testid="level-picker-sticky-save">
          <span className="text-xs text-zinc-300">
            <span className="font-display text-white">{dirtyCount}</span> pending change{dirtyCount === 1 ? "" : "s"}
          </span>
          <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white text-[10px] uppercase tracking-widest font-bold">
            {saving ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Save className="h-3 w-3 mr-2" />} Save
          </Button>
        </div>
      )}
    </div>
  );
}
